import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEventServer } from "@/lib/tracking";
import { trackLead } from "@/lib/analytics";

const STORAGE_KEY_BASE = "jb_popup_dismissed";

function fireMetaLead(email, context) {
  // Fires BOTH Meta Pixel Lead and AppLovin generate_lead.
  trackLead({ email, contentName: `Jungle Badger Deals Popup - ${context}` });
}

function getUTMs() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_medium: params.get("utm_medium") || "",
    };
  } catch (_) {
    return { utm_source: "", utm_campaign: "", utm_medium: "" };
  }
}

/**
 * Auto-triggering email capture popup.
 * `context` scopes the dismissal so a popup dismissed on the landing page
 * still fires when the visitor lands on a product page (and vice-versa).
 * `productContext` is sent to the backend for lead attribution.
 */
export default function EmailPopup({ context = "default", productContext = "" }) {
  const storageKey = `${STORAGE_KEY_BASE}_${context}`;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let dismissed = false;
    try {
      // sessionStorage so each new browser session gets a fresh popup —
      // dismissal only suppresses within the current tab/session.
      dismissed = sessionStorage.getItem(storageKey) === "1";
    } catch (_) { /* ignore */ }
    if (dismissed) return;
    const t = setTimeout(() => {
      setOpen(true);
      trackEventServer("popup_shown", productContext || "");
    }, 1500);
    return () => clearTimeout(t);
  }, [storageKey]);

  const close = () => {
    setOpen(false);
    trackEventServer("popup_dismissed", productContext || "");
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch (_) { /* ignore */ }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const utms = getUTMs();
      await api.post("/leads", {
        email,
        page: window.location.pathname,
        product_context: productContext || `${context}-popup`,
        referrer: document.referrer || "",
        ...utms,
      });
      fireMetaLead(email, context);
      trackEventServer("email_submitted", productContext || context);
      setSubmitted(true);
      try {
        sessionStorage.setItem(storageKey, "1");
      } catch (_) { /* ignore */ }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="email-popup-overlay"
      className="fixed inset-0 bg-[#1E1E24]/60 backdrop-blur-sm z-50 flex items-center justify-center p-6 sm:p-4"
      onClick={close}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-[#FFFDF9] border-4 border-[#1E1E24] rounded-3xl p-6 sm:p-8 md:p-10 max-w-sm w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="email-popup-close"
          onClick={close}
          className="absolute -top-4 -right-4 w-10 h-10 bg-white border-2 border-[#1E1E24] rounded-full flex items-center justify-center shadow-[4px_4px_0px_0px_#1E1E24] hover:bg-[#FF5A5F] hover:text-white transition-colors cursor-pointer z-10 font-bold text-lg"
          aria-label="Close"
        >
          ×
        </button>

        {!submitted ? (
          <>
            <div className="flex items-center gap-2 text-4xl mb-2" aria-hidden="true">
              <span>🦡</span>
              <span className="text-2xl">✨</span>
            </div>
            <h2 className="font-headings text-2xl sm:text-3xl md:text-4xl font-bold text-[#1E1E24] leading-tight mb-3">
              Amazon deals straight to your <span className="text-[#FF5A5F]">inbox</span>
            </h2>
            <p className="text-[#5E5E66] text-sm sm:text-base mb-3">
              Enter your email to unlock Jungle Badger deals and featured finds.
            </p>
            <p className="text-[#1E1E24] text-xs sm:text-sm font-semibold mb-5">
              You&apos;ll need to check your inbox <span className="text-[#5E5E66]">(or junk folder)</span> to confirm.
            </p>
            <form onSubmit={onSubmit} className="space-y-3">
              <input
                data-testid="email-popup-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full px-5 py-3 rounded-full border-2 border-[#1E1E24] bg-white text-[#1E1E24] placeholder-[#5E5E66] focus:outline-none focus:ring-4 focus:ring-[#FFB400] focus:ring-offset-2 focus:ring-offset-[#FFFDF9] font-medium"
              />
              {error && (
                <p data-testid="email-popup-error" className="text-[#FF5A5F] text-sm font-semibold">
                  {error}
                </p>
              )}
              <button
                data-testid="email-popup-submit"
                type="submit"
                disabled={submitting}
                className="w-full bg-[#FF5A5F] text-white border-2 border-[#1E1E24] rounded-full shadow-[4px_4px_0px_0px_#1E1E24] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_#1E1E24] transition-all font-bold tracking-wide px-8 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Confirm email"}
              </button>
              <p className="text-xs text-[#5E5E66] text-center pt-1">
                We NEVER share your data with anyone else.
              </p>
            </form>
          </>
        ) : (
          <div data-testid="email-popup-thankyou" className="text-center py-4">
            <div className="text-6xl mb-4" aria-hidden="true">📩</div>
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-[#1E1E24] leading-tight mb-3 text-balance">
              Check your email / spam folder to confirm subscription
            </h2>
            <p className="text-[#5E5E66] text-base mb-6">
              If you don&apos;t no deals will be coming your way
            </p>

            <button
              data-testid="email-popup-continue"
              onClick={close}
              className="bg-[#FFB400] text-[#1E1E24] border-2 border-[#1E1E24] rounded-full shadow-[4px_4px_0px_0px_#1E1E24] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_#1E1E24] transition-all font-bold tracking-wide px-8 py-3"
            >
              I clicked subscribe in my inbox
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
